import React, {
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export const LEDGER_SAFE_MATRIX_WINDOW_THRESHOLD = 160
export const LEDGER_SAFE_MATRIX_ESTIMATED_ROW_HEIGHT_PX = 84
export const LEDGER_SAFE_MATRIX_WINDOW_OVERSCAN_PX = 840

type LedgerSafeMatrixViewport = {
  scrollTop: number
  height: number
}

export interface LedgerSafeMatrixWindowLayout {
  startIndex: number
  endIndex: number
  topSpacerHeight: number
  bottomSpacerHeight: number
  offsets: number[]
}

const findLedgerSafeMatrixWindowIndex = (
  offsets: number[],
  targetOffset: number,
): number => {
  let low = 0
  let high = Math.max(0, offsets.length - 2)
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if ((offsets[middle + 1] ?? Number.POSITIVE_INFINITY) <= targetOffset) {
      low = middle + 1
    } else {
      high = middle
    }
  }
  return low
}

export const buildLedgerSafeMatrixWindowLayout = ({
  rowKeys,
  measuredHeightByKey,
  viewport,
}: {
  rowKeys: string[]
  measuredHeightByKey: ReadonlyMap<string, number>
  viewport: LedgerSafeMatrixViewport
}): LedgerSafeMatrixWindowLayout => {
  const offsets = [0]
  rowKeys.forEach(rowKey => {
    const measuredHeight = measuredHeightByKey.get(rowKey)
    offsets.push(offsets[offsets.length - 1] + (
      measuredHeight && measuredHeight > 0
        ? measuredHeight
        : LEDGER_SAFE_MATRIX_ESTIMATED_ROW_HEIGHT_PX
    ))
  })
  if (rowKeys.length === 0) {
    return {
      startIndex: 0,
      endIndex: 0,
      topSpacerHeight: 0,
      bottomSpacerHeight: 0,
      offsets,
    }
  }

  const minimumOffset = Math.max(0, viewport.scrollTop - LEDGER_SAFE_MATRIX_WINDOW_OVERSCAN_PX)
  const maximumOffset = viewport.scrollTop
    + Math.max(viewport.height, 640)
    + LEDGER_SAFE_MATRIX_WINDOW_OVERSCAN_PX
  const startIndex = findLedgerSafeMatrixWindowIndex(offsets, minimumOffset)
  const endIndex = Math.min(
    rowKeys.length,
    findLedgerSafeMatrixWindowIndex(offsets, maximumOffset) + 1,
  )
  const totalHeight = offsets[offsets.length - 1] || 0

  return {
    startIndex,
    endIndex,
    topSpacerHeight: offsets[startIndex] || 0,
    bottomSpacerHeight: Math.max(0, totalHeight - (offsets[endIndex] || totalHeight)),
    offsets,
  }
}

export interface LedgerSafeMatrixWindowState extends LedgerSafeMatrixWindowLayout {
  isWindowed: boolean
  measureRowGroup: (rowKey: string, height: number) => void
  scrollToRowKey: (rowKey: string) => boolean
}

export const useLedgerSafeMatrixWindow = ({
  rowKeys,
  viewportRef,
}: {
  rowKeys: string[]
  viewportRef: MutableRefObject<HTMLDivElement | null>
}): LedgerSafeMatrixWindowState => {
  const isWindowed = rowKeys.length > LEDGER_SAFE_MATRIX_WINDOW_THRESHOLD
  const measuredHeightByKeyRef = useRef(new Map<string, number>())
  const frameRef = useRef<number | null>(null)
  const rowMeasurementFrameRef = useRef<number | null>(null)
  const pendingRowHeightByKeyRef = useRef(new Map<string, number>())
  const [measurementVersion, setMeasurementVersion] = useState(0)
  const [viewport, setViewport] = useState<LedgerSafeMatrixViewport>({
    scrollTop: 0,
    height: 0,
  })

  useLayoutEffect(() => {
    const currentKeySet = new Set(rowKeys)
    let removed = false
    measuredHeightByKeyRef.current.forEach((_height, rowKey) => {
      if (!currentKeySet.has(rowKey)) {
        measuredHeightByKeyRef.current.delete(rowKey)
        removed = true
      }
    })
    if (removed) setMeasurementVersion(version => version + 1)
  }, [rowKeys])

  useLayoutEffect(() => {
    if (!isWindowed) return undefined
    const viewportElement = viewportRef.current
    if (!viewportElement) return undefined

    const measureViewport = () => {
      frameRef.current = null
      const nextViewport = {
        scrollTop: viewportElement.scrollTop,
        height: viewportElement.clientHeight,
      }
      setViewport(previous => (
        previous.scrollTop === nextViewport.scrollTop && previous.height === nextViewport.height
          ? previous
          : nextViewport
      ))
    }
    const scheduleViewportMeasurement = () => {
      if (frameRef.current === null) {
        frameRef.current = window.requestAnimationFrame(measureViewport)
      }
    }

    measureViewport()
    viewportElement.addEventListener('scroll', scheduleViewportMeasurement, { passive: true })
    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(scheduleViewportMeasurement)
    observer?.observe(viewportElement)
    window.addEventListener('resize', scheduleViewportMeasurement)
    return () => {
      viewportElement.removeEventListener('scroll', scheduleViewportMeasurement)
      observer?.disconnect()
      window.removeEventListener('resize', scheduleViewportMeasurement)
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [isWindowed, viewportRef])

  const layout = useMemo(
    () => {
      void measurementVersion
      return isWindowed
        ? buildLedgerSafeMatrixWindowLayout({
            rowKeys,
            measuredHeightByKey: measuredHeightByKeyRef.current,
            viewport,
          })
        : {
            startIndex: 0,
            endIndex: rowKeys.length,
            topSpacerHeight: 0,
            bottomSpacerHeight: 0,
            offsets: [0],
          }
    },
    [isWindowed, measurementVersion, rowKeys, viewport],
  )

  const flushRowMeasurements = useCallback(() => {
    rowMeasurementFrameRef.current = null
    let changed = false
    pendingRowHeightByKeyRef.current.forEach((height, rowKey) => {
      const previousHeight = measuredHeightByKeyRef.current.get(rowKey)
      if (previousHeight !== undefined && Math.abs(previousHeight - height) < 1) return
      measuredHeightByKeyRef.current.set(rowKey, height)
      changed = true
    })
    pendingRowHeightByKeyRef.current.clear()
    if (changed) setMeasurementVersion(version => version + 1)
  }, [])

  const measureRowGroup = useCallback((rowKey: string, height: number) => {
    if (!isWindowed || !Number.isFinite(height) || height <= 0) return
    pendingRowHeightByKeyRef.current.set(rowKey, Math.max(1, Math.round(height)))
    if (rowMeasurementFrameRef.current === null) {
      rowMeasurementFrameRef.current = window.requestAnimationFrame(flushRowMeasurements)
    }
  }, [flushRowMeasurements, isWindowed])

  useLayoutEffect(() => () => {
    pendingRowHeightByKeyRef.current.clear()
    if (rowMeasurementFrameRef.current !== null) {
      window.cancelAnimationFrame(rowMeasurementFrameRef.current)
      rowMeasurementFrameRef.current = null
    }
  }, [])

  const rowIndexByKey = useMemo(
    () => new Map(rowKeys.map((rowKey, index) => [rowKey, index])),
    [rowKeys],
  )
  const scrollToRowKey = useCallback((rowKey: string): boolean => {
    const viewportElement = viewportRef.current
    const rowIndex = rowIndexByKey.get(rowKey)
    if (!viewportElement || rowIndex === undefined) return false
    const rowStart = layout.offsets[rowIndex] ?? rowIndex * LEDGER_SAFE_MATRIX_ESTIMATED_ROW_HEIGHT_PX
    const rowEnd = layout.offsets[rowIndex + 1] ?? rowStart + LEDGER_SAFE_MATRIX_ESTIMATED_ROW_HEIGHT_PX
    viewportElement.scrollTop = Math.max(
      0,
      rowStart - Math.max(0, viewportElement.clientHeight - (rowEnd - rowStart)) / 2,
    )
    setViewport({
      scrollTop: viewportElement.scrollTop,
      height: viewportElement.clientHeight,
    })
    return true
  }, [layout.offsets, rowIndexByKey, viewportRef])

  return {
    isWindowed,
    ...layout,
    measureRowGroup,
    scrollToRowKey,
  }
}

export const LedgerSafeMatrixWindowSection = ({
  isWindowed,
  rowKey,
  onMeasure,
  children,
}: {
  isWindowed: boolean
  rowKey: string
  onMeasure: (rowKey: string, height: number) => void
  children: ReactNode
}) => {
  const sectionRef = useRef<HTMLTableSectionElement | null>(null)

  useLayoutEffect(() => {
    if (!isWindowed) return undefined
    const section = sectionRef.current
    if (!section) return undefined
    const measure = () => onMeasure(rowKey, section.getBoundingClientRect().height)
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(section)
    return () => observer?.disconnect()
  }, [isWindowed, onMeasure, rowKey])

  if (!isWindowed) return <>{children}</>

  return (
    <tbody
      ref={sectionRef}
      data-ledger-matrix-window-row={rowKey}
    >
      {children}
    </tbody>
  )
}
